/* KalenderCentral — unified calendar overview aggregating events, tasks, alarms,
 * Farmor Corgi reminders, skincare, daily-brief. Big month grid with source dots,
 * day-agenda below, and quick-add for reminders/alarms/events/tasks. */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Loader2, Clock, Bell, AlarmClock, CheckSquare, Sparkles, X, Star,
} from 'lucide-react';
import CalendarPage from './CalendarPage';
import AppleCalendarSync from './AppleCalendarSync';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SV_MONTHS = [
  'januari', 'februari', 'mars', 'april', 'maj', 'juni',
  'juli', 'augusti', 'september', 'oktober', 'november', 'december',
];
const SV_WEEKDAYS_SHORT = ['må', 'ti', 'on', 'to', 'fr', 'lö', 'sö'];
const SV_WEEKDAYS_LABELS = [
  { id: 0, s: 'Må' }, { id: 1, s: 'Ti' }, { id: 2, s: 'On' },
  { id: 3, s: 'To' }, { id: 4, s: 'Fr' }, { id: 5, s: 'Lö' }, { id: 6, s: 'Sö' },
];

const CATEGORY_OPTIONS = [
  { id: 'personal', label: 'Personligt', emoji: '💗' },
  { id: 'work',     label: 'Jobb',       emoji: '💼' },
  { id: 'kids',     label: 'Barn',       emoji: '🧸' },
  { id: 'training', label: 'Träning',    emoji: '🏃' },
  { id: 'health',   label: 'Hälsa',      emoji: '🏥' },
  { id: 'social',   label: 'Socialt',    emoji: '🎉' },
  { id: 'other',    label: 'Annat',      emoji: '🌸' },
];

const REMINDER_OPTIONS = [
  { min: 0, label: 'Vid tiden' },
  { min: 5, label: '5 min före' },
  { min: 15, label: '15 min före' },
  { min: 30, label: '30 min före' },
  { min: 60, label: '1 tim före' },
  { min: 1440, label: '1 dag före' },
  { min: 10080, label: '1 vecka före' },
];

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
}

function toIso(dt) {
  return dt.toISOString().slice(0, 10);
}

export default function KalenderCentral() {
  const [subtab, setSubtab] = useState('central');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [monthData, setMonthData] = useState(null);
  const [selectedIso, setSelectedIso] = useState(toIso(now));
  const [dayData, setDayData] = useState(null);
  const [loadingMonth, setLoadingMonth] = useState(true);
  const [loadingDay, setLoadingDay] = useState(false);
  const [addingKind, setAddingKind] = useState(null); // 'reminder' | 'alarm' | 'task'

  const loadMonth = useCallback(async () => {
    setLoadingMonth(true);
    try {
      const { data } = await axios.get(`${API}/kalender/month/${year}/${month}`, { withCredentials: true });
      setMonthData(data);
    } catch { toast.error('Kunde inte ladda kalendern'); }
    finally { setLoadingMonth(false); }
  }, [year, month]);

  const loadDay = useCallback(async () => {
    if (!selectedIso) return;
    setLoadingDay(true);
    try {
      const { data } = await axios.get(`${API}/kalender/day/${selectedIso}`, { withCredentials: true });
      setDayData(data);
    } catch { toast.error('Kunde inte ladda dagen'); }
    finally { setLoadingDay(false); }
  }, [selectedIso]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadDay(); }, [loadDay]);

  const monthLabel = `${SV_MONTHS[month - 1]} ${year}`;

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYear(y); setMonth(m);
  };

  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
    setSelectedIso(toIso(t));
  };

  const monthGrid = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(d);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const summary = monthData?.summary || {};
  const sources = monthData?.sources || {};

  const onAdded = async () => {
    setAddingKind(null);
    toast.success('Tillagt 💜');
    await Promise.all([loadMonth(), loadDay()]);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Subtab bar — three unified calendar views */}
      <div className="flex gap-1.5 flex-wrap" data-testid="kalender-subtabs">
        {[
          { id: 'central',  label: 'Översikt',          emoji: '🌸' },
          { id: 'classic',  label: 'Klassisk',          emoji: '📆' },
          { id: 'external', label: 'Externa kalendrar', emoji: '🍎' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubtab(t.id)}
            className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 ${subtab === t.id ? 'bg-[#FFE8F5]' : 'bg-white'}`}
            data-testid={`kalender-subtab-${t.id}`}
          >
            <span>{t.emoji}</span> {t.label}
          </button>
        ))}
      </div>

      {subtab === 'classic' && <CalendarPage />}
      {subtab === 'external' && <AppleCalendarSync />}
      {subtab === 'central' && (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="kalender-central">
      {/* Header */}
      <div className="rounded-3xl p-5 border relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF5F0 0%, #FFEAF3 60%, #EEF3FF 100%)', borderColor: '#F3D0D9' }}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase font-semibold tracking-widest text-rose-800/70">Kalender-Central</div>
            <h1 className="text-2xl font-bold text-rose-950 flex items-center gap-2" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
              <CalendarDays className="w-5 h-5" /> Allt på ett ställe 💗
            </h1>
            <p className="text-xs text-rose-900/70 mt-1">
              Händelser, alarm, påminnelser, rutiner och Farmors saker — samlade.
            </p>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setAddingKind('reminder')}
              data-testid="kalender-add-reminder"
              className="text-xs px-3 py-1.5 rounded-full bg-white/80 border border-rose-300 text-rose-800 hover:bg-white inline-flex items-center gap-1 shadow-sm"
            >
              <Bell className="w-3 h-3" /> Påminnelse
            </button>
            <button
              onClick={() => setAddingKind('alarm')}
              data-testid="kalender-add-alarm"
              className="text-xs px-3 py-1.5 rounded-full bg-white/80 border border-amber-300 text-amber-900 hover:bg-white inline-flex items-center gap-1 shadow-sm"
            >
              <AlarmClock className="w-3 h-3" /> Alarm
            </button>
            <button
              onClick={() => setAddingKind('task')}
              data-testid="kalender-add-task"
              className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white hover:opacity-90 inline-flex items-center gap-1 shadow"
            >
              <Plus className="w-3 h-3" /> Uppgift
            </button>
          </div>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between rounded-2xl bg-white border border-slate-100 p-3">
        <button onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-slate-50" aria-label="Föregående" data-testid="kalender-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-center">
          <div className="text-lg font-bold text-slate-900 capitalize" data-testid="kalender-month-label">{monthLabel}</div>
          <button onClick={goToday} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200 mt-1" data-testid="kalender-today">
            Idag
          </button>
        </div>
        <button onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-slate-50" aria-label="Nästa" data-testid="kalender-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Source legend */}
      <div className="flex flex-wrap gap-1 text-[10px]">
        {Object.entries(sources).map(([sid, meta]) => (
          <span
            key={sid}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border"
            style={{ backgroundColor: meta.color + '60', borderColor: meta.color }}
          >
            <span>{meta.emoji}</span> {meta.label}
          </span>
        ))}
      </div>

      {/* Month grid */}
      <div className="rounded-2xl bg-white border border-slate-100 p-3">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {SV_WEEKDAYS_SHORT.map((w) => (
            <div key={w} className="text-[10px] text-center uppercase font-bold text-slate-400">{w}</div>
          ))}
        </div>
        {loadingMonth ? (
          <div className="text-center py-6 text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /> Laddar…</div>
        ) : (
          <div className="grid grid-cols-7 gap-1" data-testid="kalender-month-grid">
            {monthGrid.map((day, idx) => {
              if (day === null) return <div key={`e${idx}`} />;
              const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const counts = summary[iso] || {};
              const total = Object.values(counts).reduce((a, b) => a + b, 0);
              const isSelected = selectedIso === iso;
              const isToday = iso === toIso(new Date());
              return (
                <button
                  key={iso}
                  onClick={() => setSelectedIso(iso)}
                  data-testid={`kalender-day-${iso}`}
                  className={`aspect-square rounded-lg text-left p-1 transition ${
                    isSelected
                      ? 'bg-gradient-to-br from-rose-100 to-fuchsia-100 border-2 border-rose-400 shadow-sm'
                      : isToday
                        ? 'bg-amber-50 border border-amber-300'
                        : 'bg-slate-50 border border-transparent hover:bg-white hover:border-slate-200'
                  }`}
                >
                  <div className={`text-xs font-semibold ${isSelected ? 'text-rose-900' : isToday ? 'text-amber-900' : 'text-slate-700'}`}>
                    {day}
                  </div>
                  {total > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {Object.entries(counts).slice(0, 4).map(([sid, count]) => (
                        <span
                          key={sid}
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: sources[sid]?.color || '#ccc' }}
                          title={`${sources[sid]?.label}: ${count}`}
                        />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Day agenda */}
      <div className="rounded-2xl bg-white border border-rose-100 p-4" data-testid="kalender-day-agenda">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase font-semibold tracking-widest text-rose-800/70">Vald dag</div>
            <div className="text-lg font-bold text-slate-900">
              {selectedIso && new Date(selectedIso).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
          {dayData && (
            <span className="text-[11px] text-slate-500">
              {dayData.items?.length || 0} saker
            </span>
          )}
        </div>

        {loadingDay ? (
          <div className="text-center py-3 text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /> Laddar…</div>
        ) : dayData?.items?.length ? (
          <div className="space-y-1.5">
            {dayData.items.map((it, idx) => {
              const meta = sources[it.source] || {};
              return (
                <div
                  key={`${it.source}-${it.id}-${idx}`}
                  className="flex items-start gap-2 p-2 rounded-lg border"
                  style={{ backgroundColor: meta.color + '25', borderColor: meta.color + '80' }}
                  data-testid={`kalender-item-${it.source}-${idx}`}
                >
                  <div className="w-14 text-right flex-shrink-0">
                    <div className="text-xs font-bold tabular-nums text-slate-900">{it.all_day ? 'hela dagen' : fmtTime(it.at)}</div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">{meta.label}</div>
                  </div>
                  <div className="text-lg leading-none flex-shrink-0">{meta.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">{it.title}</div>
                    {(it.reminders?.length > 0 || it.person || it.time_hhmm) && (
                      <div className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-1.5">
                        {it.reminders?.length > 0 && (
                          <span className="inline-flex items-center gap-0.5">
                            <Bell className="w-2.5 h-2.5" /> {it.reminders.map((m) => (REMINDER_OPTIONS.find(o => o.min === m)?.label || `${m}min`)).join(', ')}
                          </span>
                        )}
                        {it.person && <span>· {it.person}</span>}
                      </div>
                    )}
                  </div>
                  {it.important && <Star className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic text-center py-3">
            Ingenting inplanerat den här dagen. Lägg till en påminnelse eller uppgift 💜
          </div>
        )}
      </div>

      {/* Quick-add modals */}
      {addingKind === 'reminder' && (
        <QuickReminderModal defaultDate={selectedIso} onClose={() => setAddingKind(null)} onAdded={onAdded} />
      )}
      {addingKind === 'alarm' && (
        <QuickAlarmModal onClose={() => setAddingKind(null)} onAdded={onAdded} />
      )}
      {addingKind === 'task' && (
        <QuickTaskModal defaultDate={selectedIso} onClose={() => setAddingKind(null)} onAdded={onAdded} />
      )}
    </div>
      )}
    </div>
  );
}

function QuickReminderModal({ defaultDate, onClose, onAdded }) {
  const [title, setTitle] = useState('');
  const [dateIso, setDateIso] = useState(defaultDate);
  const [timeHhmm, setTimeHhmm] = useState('09:00');
  const [reminderMin, setReminderMin] = useState(15);
  const [category, setCategory] = useState('personal');
  const [important, setImportant] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (title.trim().length < 2) { toast.error('Skriv en titel'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/kalender/quick/reminder`, {
        title, date_iso: dateIso, time_hhmm: timeHhmm,
        reminder_minutes: reminderMin, important, category,
      }, { withCredentials: true });
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Ny påminnelse 💜" testId="quick-reminder-modal">
      <div className="space-y-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="T.ex. Läkarbesök"
          className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-rose-200"
          data-testid="quick-reminder-title" autoFocus />
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={dateIso} onChange={(e) => setDateIso(e.target.value)}
            className="text-sm rounded-lg border border-slate-200 px-2 py-1.5" data-testid="quick-reminder-date" />
          <input type="time" value={timeHhmm} onChange={(e) => setTimeHhmm(e.target.value)}
            className="text-sm rounded-lg border border-slate-200 px-2 py-1.5" data-testid="quick-reminder-time" />
        </div>
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Kategori</div>
          <div className="flex flex-wrap gap-1">
            {CATEGORY_OPTIONS.map((c) => (
              <button key={c.id} onClick={() => setCategory(c.id)}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${
                  category === c.id ? 'bg-rose-500 text-white border-rose-500' : 'bg-white border-slate-200 text-slate-600'
                }`}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Påminnelse</div>
          <select value={reminderMin} onChange={(e) => setReminderMin(parseInt(e.target.value, 10))}
            className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5" data-testid="quick-reminder-minutes">
            {REMINDER_OPTIONS.map((o) => <option key={o.min} value={o.min}>{o.label}</option>)}
          </select>
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs">
          <input type="checkbox" checked={important} onChange={(e) => setImportant(e.target.checked)}
            data-testid="quick-reminder-important" />
          <Star className="w-3 h-3 text-amber-500" /> Viktig — visa högst upp
        </label>
      </div>
      <ModalActions onCancel={onClose} onSave={save} saving={saving} testIdPrefix="quick-reminder" />
    </ModalShell>
  );
}

function QuickAlarmModal({ onClose, onAdded }) {
  const [label, setLabel] = useState('');
  const [timeHhmm, setTimeHhmm] = useState('07:30');
  const [days, setDays] = useState([0, 1, 2, 3, 4]); // Mon-Fri
  const [saving, setSaving] = useState(false);

  const toggleDay = (dId) => {
    setDays((prev) => prev.includes(dId) ? prev.filter((d) => d !== dId) : [...prev, dId]);
  };

  const save = async () => {
    if (label.trim().length < 2) { toast.error('Ge alarmet ett namn'); return; }
    if (days.length === 0) { toast.error('Välj minst en dag'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/kalender/quick/alarm`, { label, time_hhmm: timeHhmm, days }, { withCredentials: true });
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Nytt alarm ⏰" testId="quick-alarm-modal">
      <div className="space-y-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="T.ex. Väckarklocka jobb"
          className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-amber-200"
          data-testid="quick-alarm-label" autoFocus />
        <input type="time" value={timeHhmm} onChange={(e) => setTimeHhmm(e.target.value)}
          className="w-full text-sm rounded-lg border border-slate-200 px-2 py-1.5" data-testid="quick-alarm-time" />
        <div>
          <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Dagar</div>
          <div className="flex gap-1">
            {SV_WEEKDAYS_LABELS.map((w) => (
              <button key={w.id} onClick={() => toggleDay(w.id)}
                data-testid={`quick-alarm-day-${w.id}`}
                className={`w-9 h-9 rounded-full text-xs font-semibold border-2 ${
                  days.includes(w.id) ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-slate-200 text-slate-500'
                }`}>
                {w.s}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-slate-500 italic">
          💡 Alarmet återkommer varje vald veckodag. Öppna Foto-alarm-fliken för att koppla ett fotoobjekt att kolla av innan snooze.
        </p>
      </div>
      <ModalActions onCancel={onClose} onSave={save} saving={saving} testIdPrefix="quick-alarm" />
    </ModalShell>
  );
}

function QuickTaskModal({ defaultDate, onClose, onAdded }) {
  const [text, setText] = useState('');
  const [dateIso, setDateIso] = useState(defaultDate);
  const [timeHhmm, setTimeHhmm] = useState('');
  const [emoji, setEmoji] = useState('✨');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (text.trim().length < 2) { toast.error('Skriv en uppgift'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/kalender/quick/task`, {
        text, date_iso: dateIso, time_hhmm: timeHhmm || null, emoji,
      }, { withCredentials: true });
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte spara'); }
    finally { setSaving(false); }
  };

  return (
    <ModalShell onClose={onClose} title="Ny uppgift ✨" testId="quick-task-modal">
      <div className="space-y-2">
        <div className="flex gap-2">
          <input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4}
            className="w-14 text-center text-lg rounded-lg border border-slate-200 px-2 py-2" />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Vad ska göras?"
            className="flex-1 text-sm rounded-lg border border-slate-200 px-3 py-2 focus:ring-2 focus:ring-rose-200"
            data-testid="quick-task-text" autoFocus />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" value={dateIso} onChange={(e) => setDateIso(e.target.value)}
            className="text-sm rounded-lg border border-slate-200 px-2 py-1.5" data-testid="quick-task-date" />
          <input type="time" value={timeHhmm} onChange={(e) => setTimeHhmm(e.target.value)}
            placeholder="Valfritt"
            className="text-sm rounded-lg border border-slate-200 px-2 py-1.5" data-testid="quick-task-time" />
        </div>
        <p className="text-[10px] text-slate-500 italic">
          💡 Utan tid = uppgift utan påminnelse. Med tid = notis vid tiden.
        </p>
      </div>
      <ModalActions onCancel={onClose} onSave={save} saving={saving} testIdPrefix="quick-task" />
    </ModalShell>
  );
}

function ModalShell({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose} data-testid={testId}>
      <div className="rounded-3xl p-5 max-w-sm w-full bg-white shadow-2xl border border-rose-200"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onCancel, onSave, saving, testIdPrefix }) {
  return (
    <div className="flex justify-end gap-2 mt-4">
      <button onClick={onCancel}
        data-testid={`${testIdPrefix}-cancel`}
        className="text-xs px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-50">
        Avbryt
      </button>
      <button onClick={onSave} disabled={saving}
        data-testid={`${testIdPrefix}-save`}
        className="text-xs px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1">
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
        Spara
      </button>
    </div>
  );
}
